import { sha256 } from "js-sha256";
import { getClientCookie, COOKIE } from "#shared/utils/cookie";
import { TiktokCAPI } from "@/services/tiktokCapi";
import { mergeDeep } from "@zotlo-shared/utils/helpers";

type TiktokInitPayload = {
  pixelId: number | string;
  debug?: boolean;
  pageSlug: string;
  countryCode: string;
  ipAddress: string;
  context: Record<string, any>;
}

const logSyle =
  "background: #2E495E;border-radius: 0.5em;color: white;font-weight: bold;padding: 2px 0.5em;";

export const Tiktok = {
  options: {
    id: null as null | string | number,
    debug: false,
    pageSlug: '',
    countryCode: '',
    ipAddress: ''
  },

  // Temporary store for events triggered before cookie consent is given
  temp: [] as any[],

  log(...args: any[]) {
    if (this.options.debug && !import.meta.server) {
      console.log("%cTiktok Pixel", logSyle, ...args);
    }
  },

  getSessionId() {
    return getClientCookie(COOKIE.SESSION_ID) || ''
  },

  getExternalId() {
    return sha256(this.getSessionId());
  },

  getEventId(eventName: string) {
    return this.getSessionId() + '_' + eventName;
  },

  loadTemp() {
    for (const args of this.temp) {
      (globalThis as any)?.ttq?.[args[0]]?.(...args[1]);
    }
  },

  getAMData() {
    return {
      external_id: this.getExternalId(),
      country: this.options.countryCode,
      /* ip: this.options.ipAddress,
      user_agent: globalThis?.navigator.userAgent, */
    }
  },

  event(eventName: any, params?: any, thirdArgs?: Record<string, any>, ...restOfArgs: any[]) {
    const cookieConsent = parseValue(getClientCookie(COOKIE.COOKIE_CONSENT));
    const paramObj = mergeDeep({...(params || {})}, this.getAMData());
    const thirdArgsObj = mergeDeep({...(thirdArgs || {})}, { event_id: this.getEventId(eventName) });
    const argList = [eventName, paramObj, thirdArgsObj, ...restOfArgs];

    (globalThis as any)?.ttq?.track?.(...argList);

    if (!cookieConsent && eventName !== 'Pageview') {
      this.temp.push(['track', argList]);
    }

    this.log("event", argList);
  },

  track(...args: any[]) {
    this.event(...(args as Parameters<typeof this.event>));
  },

  identify(payload: {
    email?: string;
    phone_number?: string;
  }) {
    const cookieConsent = parseValue(getClientCookie(COOKIE.COOKIE_CONSENT));
    const paramObj = mergeDeep({ ...(payload || {}) }, {
      external_id: this.getExternalId(),
    });

    (globalThis as any)?.ttq?.identify?.(paramObj);

    if (!cookieConsent) {
      this.temp.push(['identify', [paramObj]]);
    }

    this.log("identify", paramObj);
  },

  purchase(payload: {
    value: any;
    currency: any;
    description: string;
    content_id: string;
    orderID: string;
    context: Record<string, any>;
    contents?: {
      content_id: string;
      content_name: string;
      price: number;
      quantity: number;
    }[];
  }) {
    this.event('Purchase', {
      quantity: 1,
      content_type: 'product',
      ...payload
    });
  },

  prepareCAPIParams(siteUrl: string, ttParams?: Record<COOKIE.TTCLICK_ID | COOKIE.TTBROWSER_ID, string>) {
    ttParams = ttParams || {
      [COOKIE.TTCLICK_ID]: getClientCookie(COOKIE.TTCLICK_ID) || '',
      [COOKIE.TTBROWSER_ID]: getClientCookie(COOKIE.TTBROWSER_ID) || ''
    };

    const location = new URL(`${siteUrl.startsWith('http') ? siteUrl : `https://${siteUrl}`}`);
    const { ttclid } = parseQueryString(location?.search || '');

    if (!ttParams[COOKIE.TTCLICK_ID] && ttclid) {
      // const subdomainIndex = 1;
      /* let subdomainIndex = location.host.split('.').length - 1;
      if (subdomainIndex > 2) subdomainIndex = 2;
      if (subdomainIndex < 0) subdomainIndex = 0; */
  
      ttParams[COOKIE.TTCLICK_ID] = ttclid || '';
    }

    return ttParams;
  },

  createCAPIClickID(params: { siteUrl?: string; value?: string; pageSlug: string; }) {
    const { value, pageSlug } = params;

    return {
      cookieName: COOKIE.TTCLICK_ID,
      exdays: generateExpireTime(new Date(new Date().setDate(new Date().getDate() + 90))).date, // 90 days
      value: value || '',
      path: pageSlug
    }
  },

  async sendCAPIInfo() {
    const win = (globalThis as any);

    setTimeout(() => {
      const params = Tiktok.prepareCAPIParams(window.location.href);
      const hasAnyValue = !!Object.values(params).filter(Boolean).length;

      if (hasAnyValue) {
        TiktokCAPI.send({
          ttp: params[COOKIE.TTBROWSER_ID]
        }, { hideErrorToast: true });
      }
    }, 1000);

    // Delete temp proxy object
    if (win?.proxy1) delete win.proxy1;

    // Destroy listener event
    win?.__EMITTER__?.off('tiktokLoad');
  },

  init(payload: TiktokInitPayload) {
    const headScripts = {
      script: [] as any[],
      noscript: [] as any[]
    };

    if (import.meta.server) return headScripts;
    if (this.options.id) return headScripts;
    if (!payload.pixelId) {
      console.warn("Tiktok Pixel cannot be installed because there is no Pixel ID!");
      return headScripts;
    }

    this.options.id = payload.pixelId;
    this.options.debug = !!payload.debug;
    this.options.pageSlug = payload.pageSlug;
    this.options.countryCode = payload.countryCode;
    this.options.ipAddress = payload.ipAddress;

    if (this.options.debug) {
      this.track('load', this.options.id);
      this.track('page');
      return headScripts;
    }

    if (import.meta.client) {
      const win = globalThis as any;
      win?.__EMITTER__?.on('tiktokLoad', Tiktok.sendCAPIInfo.bind(this));
    }

    if (globalThis.document) {
      (function (w: any, d: any, t: any, id: any, sid: any, ldtmp: any) {
        w.TiktokAnalyticsObject=t;
        var ttq=w[t]=w[t]||[];
        ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","revokeConsent","grantConsent"],
        ttq.setAndDefer=function(t: any,e: any){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
        for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
        ttq.instance=function(t: any){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},
        ttq.load=function(e: any,n: any){
          var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},
          ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,
          o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a?.parentNode?.insertBefore(o,a);
          var win = globalThis as any;
          if (!win?.__EMITTER__) return false;
          win?.__EMITTER__.emit('tiktokLoad');
          if (win) {
            win.loadedIntegrations=win.loadedIntegrations||[];
            win.loadedIntegrations.push('Tiktok');
          }
        };
        ttq.load(id, { historyObserver: false });
        ttq.page(sid);
        ttq.identify({ external_id: sid.external_id });
        if (ldtmp) ldtmp();
      }(globalThis, document, 'ttq', this.options.id, {...this.getAMData(), event_id: this.getEventId('page') }, () => this.loadTemp()));
    }

    return headScripts
  },

  initOnSSR(payload: TiktokInitPayload) {
    const headScripts = {
      script: [] as any[],
      noscript: [] as any[]
    };

    if (!payload.pixelId) {
      console.warn("Tiktok Pixel cannot be installed because there is no Pixel ID!");
      return headScripts;
    }

    this.options.id = payload.pixelId;
    this.options.debug = !!payload.debug;
    this.options.pageSlug = payload.pageSlug;
    this.options.countryCode = payload.countryCode;
    this.options.ipAddress = payload.ipAddress;

    if (this.options.debug) {
      this.track('load', this.options.id);
      this.track('page');
      return headScripts;
    }

    if (import.meta.client) {
      const win = globalThis as any;
      win?.__EMITTER__?.on('tiktokLoad', Tiktok.sendCAPIInfo.bind(this));
      /*
        Trigger tiktok load with proxy object.
        This is necessary because script load on SSR and till then, window.__EMITTER__ is not set to window object yet.
        When load this initOnSSR script, check proxy object on window.
        If object found, that means script loaded.
        After that, set object as true to trigger event by proxy handler below
      */
      if (win?.proxy2) win.proxy2.tiktokload = true; // Trigger listener
    }

    headScripts.script.push({
      innerHTML: `!function (w, d, t) {
      w.TiktokAnalyticsObject=t;
      var ttq=w[t]=w[t]||[];
      ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","revokeConsent","grantConsent"],
      ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
      for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
      ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},
      ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,
      ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",
      o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a);
      w.loadedIntegrations=w.loadedIntegrations||[];
      w.loadedIntegrations.push('Tiktok');
      window.proxy2 = new Proxy({ tiktokload: false },{
      set(obj, prop, value) {obj[prop] = value;
      if(window.__EMITTER__) {window.__EMITTER__.emit("tiktokLoad", value);}return true;}});};
      ttq.load('${this.options.id}', { historyObserver: false });
      ttq.page(${JSON.stringify({...this.getAMData(), event_id: this.getEventId('page') })});
      ttq.identify({ external_id: '${this.getExternalId()}' });
      }(window, document, 'ttq');`,
    });

    return headScripts;
  }
}
